import React, { useState } from "react";
import {
  SafeAreaView,
  View,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { router, useLocalSearchParams } from "expo-router";
import { FontFamilies } from "@/constants/Typography";
import { AppText } from "@/components/AppText";
import { useBills } from "@/hooks/useBills";
import Header from "@/components/ui/Header";
import { PhoneRefundFlowModal } from "@/components/hotelReservation/PhoneRefundFlowModal";
import { BillWithRefundProcess, UserBill } from "@/types/bill";
import { showError } from "@/utils/toast";
import dayjs from "dayjs";
import { useUserProfile, useUpdateUserProfile } from "@/store/userProfile";
import { OfficeHoursModal } from "@/components/OfficeHoursModal";
import { isWithinBusinessHours } from "@/utils/business-hours";
import PinConfirmationModal from "@/components/bills/PinConfirmationModal";

const BillDetailScreen: React.FC = () => {
  const insets = useSafeAreaInsets();
  const { billId } = useLocalSearchParams<{ billId: string }>();
  const { bills, refetch } = useBills();
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showOfficeHoursModal, setShowOfficeHoursModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const {
    data: profile,
    isLoading: profileLoading,
    refetch: refetchProfile,
  } = useUserProfile();
  const updateProfileMutation = useUpdateUserProfile();

  const bill = bills.find(
    (b) =>
      `${b.accountNumber}#${b.billingDate}` === decodeURIComponent(billId || "")
  );

  if (!bill) {
    return (
      <SafeAreaView style={styles.container}>
        <Header title="Bill details" />
        <View style={styles.centerContainer}>
          <AppText style={styles.errorText}>Bill not found</AppText>
        </View>
      </SafeAreaView>
    );
  }

  const convertBillToRefundProcessItem = (
    bill: UserBill | undefined
  ): BillWithRefundProcess | null => {
    if (!bill) return null;

    return {
      ...bill,
      currentStatus: null,
      reasonForIntervention: null,
      refundProcessPk: undefined,
      refundProcessSk: undefined,
    };
  };

  const handleInitiateRefund = () => {
    if (!bill?.accountNumber || !bill?.billingDate) {
      showError("Account number or billing date not found for this bill");
      return;
    }

    // Check if within business hours
    if (!isWithinBusinessHours()) {
      setShowOfficeHoursModal(true);
      return;
    }

    // Show PIN modal before starting the call
    setShowPinModal(true);
  };

  const handleCallAnyway = () => {
    setShowOfficeHoursModal(false);
    setShowRefundModal(true);
  };

  const handleCancelOfficeHours = () => {
    setShowOfficeHoursModal(false);
  };

  const handlePinConfirm = async (pin: string) => {
    setShowPinModal(false);

    // Update profile with PIN if provided
    if (pin) {
      try {
        const currentAdditionalDetails = profile?.additionalDetails || "";
        const updatedAdditionalDetails = currentAdditionalDetails
          ? `${currentAdditionalDetails}\nAccount PIN: ${pin}`
          : `Account PIN: ${pin}`;

        await updateProfileMutation.mutateAsync({
          additionalDetails: updatedAdditionalDetails,
        });
      } catch (error) {
        console.error("Error saving PIN:", error);
        showError("Failed to save PIN. Proceeding without it.");
      }
    }

    // Proceed with the call
    setShowRefundModal(true);
  };

  const handlePinSkip = () => {
    setShowPinModal(false);
    setShowRefundModal(true);
  };

  const hasRequiredProfileFields = (profile: any) => {
    return !!(profile?.firstName && profile?.lastName && profile?.phoneNumber);
  };

  const handleConfirm = async () => {
    try {
      // Refetch profile to ensure we have latest data
      await refetchProfile();

      // Check if profile exists and has required fields
      if (!profile || !hasRequiredProfileFields(profile)) {
        // Navigate to profile info screen if profile is incomplete
        router.push("/bills/profile-info");
      } else {
        // Proceed with refund if profile is complete
        handleInitiateRefund();
      }
    } catch (error) {
      console.error("Error checking profile:", error);
      showError("Failed to check profile. Please try again.");
    }
  };

  const handleCancel = () => {
    router.replace("/bills/upload");
  };

  const bottomActionsStyle = [
    styles.bottomActions,
    { paddingBottom: insets.bottom === 0 ? 16 : insets.bottom },
  ];

  return (
    <SafeAreaView style={styles.container}>
      <Header title="Bill details" onBackPress={handleCancel} />

      <ScrollView
        style={styles.content}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: Math.max(insets.bottom, 34) + 161 },
        ]}
        showsVerticalScrollIndicator={false}
      >
        {/* Header Section */}
        <View style={styles.headerSection}>
          <AppText style={styles.headerTitle}>Bill details</AppText>
          <AppText style={styles.headerDescription}>
            Review your bill information below. We&apos;ll use this only to negotiate
            lower rates with your provider.
          </AppText>
        </View>

        {/* Bill Information Card */}
        <View style={styles.billCard}>
          <View style={styles.billCardContent}>
            {/* Card Header */}
            <View style={styles.cardHeader}>
              <View style={styles.cardHeaderLeft}>
                <AppText style={styles.cardTitle}>Bill Information</AppText>
              </View>
              <View style={styles.cardHeaderRight}>
                <AppText style={styles.billAmount}>
                  ${bill.totalAmount.toFixed(0)}
                </AppText>
              </View>
            </View>

            {/* Divider */}
            <View style={styles.divider} />

            {/* Details */}
            <View style={styles.detailsContainer}>
              <View style={styles.detailsColumn}>
                <View style={styles.detailRow}>
                  <AppText style={styles.detailLabel}>Account Number</AppText>
                </View>
                <View style={styles.detailRow}>
                  <AppText style={styles.detailLabel}>Billing Date</AppText>
                </View>
                <View style={styles.detailRow}>
                  <AppText style={styles.detailLabel}>Due Date</AppText>
                </View>
                <View style={styles.detailRow}>
                  <AppText style={styles.detailLabel}>Billing Period</AppText>
                </View>
              </View>

              <View style={styles.valuesColumn}>
                <View style={styles.detailRow}>
                  <AppText style={styles.detailValue}>
                    ****{bill.accountNumber.slice(-4)}
                  </AppText>
                </View>
                <View style={styles.detailRow}>
                  <AppText style={styles.detailValue}>
                    {dayjs(bill.billingDate).format("MMM D, YYYY")}
                  </AppText>
                </View>
                <View style={styles.detailRow}>
                  <AppText style={styles.detailValue}>
                    {dayjs(bill.dueDate).format("MMM D, YYYY")}
                  </AppText>
                </View>
                <View style={styles.detailRow}>
                  <AppText style={styles.detailValue}>
                    {dayjs(bill.billingPeriod.startDate).format("MMM D")} –{" "}
                    {dayjs(bill.billingPeriod.endDate).format("MMM D, YYYY")}
                  </AppText>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Privacy Note */}
        <AppText style={styles.privacyNote}>
          *We&apos;ll use this information only to negotiate lower rates with your
          provider. Your data is always private.
        </AppText>
      </ScrollView>

      {/* Bottom Actions */}
      <View style={bottomActionsStyle}>
        <View style={styles.buttonContainer}>
          <TouchableOpacity
            style={[
              styles.confirmButton,
              profileLoading && styles.disabledButton,
            ]}
            onPress={handleConfirm}
            activeOpacity={0.8}
            disabled={profileLoading}
          >
            <AppText style={styles.confirmButtonText}>
              {profileLoading ? "Loading..." : "Confirm & Continue"}
            </AppText>
          </TouchableOpacity>
        </View>

        <View style={styles.cancelContainer}>
          <TouchableOpacity onPress={handleCancel} activeOpacity={0.8}>
            <AppText style={styles.cancelText}>Cancel</AppText>
          </TouchableOpacity>
        </View>
      </View>

      {/* PIN Confirmation Modal */}
      <PinConfirmationModal
        visible={showPinModal}
        onConfirm={handlePinConfirm}
        onSkip={handlePinSkip}
        onClose={() => setShowPinModal(false)}
      />

      {/* Refund Modal */}
      {convertBillToRefundProcessItem(bill) && (
        <PhoneRefundFlowModal
          visible={showRefundModal}
          item={convertBillToRefundProcessItem(bill)!}
          autoStart={true}
          onClose={() => {
            setShowRefundModal(false);
            refetch();
          }}
        />
      )}

      {/* Office Hours Warning Modal */}
      <OfficeHoursModal
        visible={showOfficeHoursModal}
        onCallAnyway={handleCallAnyway}
        onCancel={handleCancelOfficeHours}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 24,
  },
  centerContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorText: {
    fontSize: 16,
    color: "#6B7280",
    fontFamily: FontFamilies.Inter.Regular,
  },
  headerSection: {
    gap: 8,
    marginBottom: 24,
  },
  headerTitle: {
    fontSize: 24,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
    lineHeight: 33,
  },
  headerDescription: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "rgba(22, 29, 20, 0.7)",
    lineHeight: 23,
  },
  billCard: {
    backgroundColor: "#F4F5F6",
    borderRadius: 8,
    padding: 20,
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  billCardContent: {
    gap: 16,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  cardHeaderLeft: {
    flex: 1,
  },
  cardHeaderRight: {
    width: 80,
    alignItems: "flex-end",
  },
  cardTitle: {
    fontSize: 16,
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: "#161D14",
    lineHeight: 20,
  },
  billAmount: {
    fontSize: 16,
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: "#161D14",
    lineHeight: 20,
  },
  divider: {
    height: 1,
    backgroundColor: "#EBEBEB",
    marginVertical: 0,
  },
  detailsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 16,
  },
  detailsColumn: {
    flex: 1,
    gap: 16,
  },
  valuesColumn: {
    width: 167,
    gap: 16,
    alignItems: "flex-end",
  },
  detailRow: {
    height: 23,
    justifyContent: "center",
  },
  detailLabel: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    lineHeight: 23,
  },
  detailValue: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    lineHeight: 23,
    textAlign: "right",
  },
  privacyNote: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Regular,
    color: "rgba(22, 29, 20, 0.5)",
    lineHeight: 19,
  },
  bottomActions: {
    alignItems: "center",
    gap: 12,
    paddingTop: 24,
  },
  buttonContainer: {
    width: 343,
  },
  confirmButton: {
    backgroundColor: "#0F9129",
    borderRadius: 100,
    paddingVertical: 16.5,
    paddingHorizontal: 20,
    alignItems: "center",
    justifyContent: "center",
    height: 58,
  },
  confirmButtonText: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 17,
    lineHeight: 27,
    color: "#FFFFFF",
  },
  cancelContainer: {
    alignItems: "center",
  },
  cancelText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    lineHeight: 23,
    color: "#414141",
  },
  disabledButton: {
    opacity: 0.6,
  },
});

export default BillDetailScreen;
